import { useEffect, useId, useState } from "react";
import type { JSX } from "react";
import type { CrewMember } from "../../api/crewSchemas";
import type { ClockState } from "../../domain/clock";
import { toEpochSeconds } from "../../domain/time";
import type { LocalDate } from "../../domain/time";
import type { LocalEvent } from "../../offline/model";
import { useLocalEvents, useOffline } from "../../offline/OfflineContext";
import { usePurchasePaths } from "../../platform/PlatformContext";
import { useI18n } from "../../shared/i18n/I18nContext";
import { Alert } from "../../shared/ui/Alert";
import { Badge } from "../../shared/ui/Badge";
import type { BadgeTone } from "../../shared/ui/Badge";
import { Checkbox } from "../../shared/ui/Checkbox";
import { cx } from "../../shared/ui/classNames";
import { describeErrorCode } from "../../shared/ui/describeError";
import { ErrorMessage } from "../../shared/ui/ErrorMessage";
import { Field } from "../../shared/ui/Field";
import { formatDateTime, formatTimeOrDateTime } from "../../shared/ui/format";
import { Skeleton } from "../../shared/ui/Skeleton";
import styles from "../../shared/ui/ui.module.css";
import clockStyles from "./ClockPage.module.css";
import {
  crewConflicts,
  crewEntries,
  crewSteps,
  matchingTargets,
  targetsOf,
  withShown,
} from "./model/crewActions";
import type { CrewEntry } from "./model/crewActions";
import type { ClockAction } from "./model/clockActions";
import { useCrew } from "./useCrew";
import { useCrewRecording } from "./useCrewRecording";

/** From this many persons on, the crew offers a search. */
const searchFromCount = 9;

/** Colour of a clock state (design-guidelines.md 1), like the team overview. */
const stateTone: Readonly<Record<ClockState, BadgeTone>> = {
  Off: "neutral",
  Working: "success",
  OnBreak: "info",
};

/** Device setting that remembers whether the crew is open, per owner key. */
const openSettingPrefix = "crewOpen:";

/** Properties of {@link CrewClock}. */
export interface CrewClockProps {
  /** Owner key of the session. */
  readonly owner: string;
  /** Time zone of the recording person, for persons without employment zone and the times shown. */
  readonly timeZone: string;
  /** Today in that time zone. */
  readonly today: LocalDate;
}

/**
 * Clocking for others on the clock screen ("Kolonne", T-170, ADR-0139): a manager chooses persons of
 * the own team (also as a deputy), an HR administrator persons of the whole tenant, and records Kommen,
 * Pause, Weiter or Gehen for all of them at once – one event per person whose state allows the step,
 * stored in the outbox and synchronized like own events, also offline with the last known crew. The
 * affected person sees the events marked „erfasst von …“ in „Meine Zeiten“. The crew is a disclosure:
 * it is loaded and refreshed only while it is open, and the device remembers whether it was open.
 *
 * @param props - Component properties.
 * @returns The section.
 */
export function CrewClock(props: CrewClockProps): JSX.Element {
  const { t } = useI18n();
  const { store } = useOffline();
  const [open, setOpen] = useState(false);
  const headingId = useId();
  const setting = `${openSettingPrefix}${props.owner}`;

  useEffect(() => {
    let active = true;
    store.getSetting(setting).then(
      (value) => {
        if (active && value === "1") {
          setOpen(true);
        }
      },
      () => undefined,
    );
    const cleanup = (): void => {
      active = false;
    };

    return cleanup;
  }, [store, setting]);

  const section = (
    <section className={cx(styles["card"], styles["stack"])} aria-labelledby={headingId}>
      <h2 id={headingId}>{t("crew.title")}</h2>
      <p className={styles["muted"]}>{t("crew.intro")}</p>
      <details
        open={open}
        onToggle={(event) => {
          const next = event.currentTarget.open;
          setOpen(next);
          void store.putSetting(setting, next ? "1" : "0").catch(() => undefined);
        }}
      >
        <summary>{t("crew.open")}</summary>
        {open ? <CrewPanel {...props} /> : null}
      </details>
    </section>
  );

  return section;
}

/**
 * The open crew: persons, choice, steps, rejected entries and the time of the list.
 *
 * @param props - Component properties.
 * @returns The panel.
 */
function CrewPanel(props: CrewClockProps): JSX.Element {
  const { t, languageTag } = useI18n();
  const crew = useCrew(true);
  const localEvents = useLocalEvents(props.owner);
  const recording = useCrewRecording(props);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = useState("");

  const timeText = (seconds: number): string =>
    formatTimeOrDateTime(seconds, props.today, languageTag, props.timeZone);
  const actionLabel = (step: ClockAction): string =>
    step.breakQualification === null
      ? t(`clock.action.${step.kind}`)
      : t(`clock.breakAction.${step.breakQualification}`);

  if (crew.data === undefined) {
    const pending = crew.isError ? (
      <ErrorMessage
        error={crew.error}
        retrying={crew.isFetching}
        onRetry={() => {
          void crew.refetch();
        }}
      />
    ) : (
      <Skeleton shape="list" count={2} />
    );
    return pending;
  }

  const { fetchedAtMs, fromDevice } = crew.data;
  const members = crew.data.crew.members;
  const entries = crewEntries(members, fetchedAtMs, localEvents.events);
  const chosen = entries.filter((entry) => selected.has(entry.member.employeeId));
  const shown = matchingTargets(entries, search);

  const toggle = (employeeId: string, checked: boolean): void => {
    const next = new Set(selected);

    if (checked) {
      next.add(employeeId);
    } else {
      next.delete(employeeId);
    }

    setSelected(next);
  };

  const panel = (
    <div className={styles["stack"]}>
      {members.length === 0 ? (
        <p className={styles["muted"]}>{t("crew.empty")}</p>
      ) : (
        <>
          {members.length >= searchFromCount ? (
            <Field label={t("crew.search")}>
              {(control) => (
                <input
                  {...control}
                  type="search"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                  }}
                />
              )}
            </Field>
          ) : null}
          <fieldset className={styles["stack"]}>
            <legend>{t("crew.people", { count: chosen.length })}</legend>
            <ul className={clockStyles["list"]}>
              {shown.map((entry) => (
                <CrewRow
                  key={entry.member.employeeId}
                  entry={entry}
                  checked={selected.has(entry.member.employeeId)}
                  timeText={timeText}
                  onChange={(checked) => {
                    toggle(entry.member.employeeId, checked);
                  }}
                />
              ))}
            </ul>
          </fieldset>
          <div className={styles["row"]}>
            <button
              type="button"
              className={styles["button"]}
              disabled={shown.length === 0}
              onClick={() => {
                setSelected(withShown(selected, shown));
              }}
            >
              {t("crew.selectShown")}
            </button>
            <button
              type="button"
              className={styles["button"]}
              disabled={chosen.length === 0}
              onClick={() => {
                setSelected(new Set());
              }}
            >
              {t("crew.selectNone")}
            </button>
          </div>
          <div className={styles["row"]}>
            {crewSteps.map((step) => {
              const count = targetsOf(step, chosen).length;
              const button = (
                <button
                  key={`${step.kind}-${step.breakQualification ?? ""}`}
                  type="button"
                  className={cx(
                    styles["button"],
                    step.kind === "ClockIn" ? styles["primary"] : undefined,
                  )}
                  disabled={recording.busy || count === 0}
                  onClick={() => {
                    void recording.record(step, chosen);
                  }}
                >
                  {t("crew.action", { action: actionLabel(step), count })}
                </button>
              );

              return button;
            })}
          </div>
          {recording.failed ? <Alert tone="danger">{t("crew.recordFailed")}</Alert> : null}
          {recording.skippedNames.length === 0 ? null : (
            <Alert tone="warning">
              {t("crew.skipped", { names: recording.skippedNames.join(", ") })}
            </Alert>
          )}
        </>
      )}

      <CrewConflicts conflicts={crewConflicts(localEvents.events)} members={members} />
      <p className={cx(styles["muted"], clockStyles["footnote"])}>
        {t(fromDevice ? "crew.fromDevice" : "clock.snapshotFrom", {
          time: timeText(toEpochSeconds(fetchedAtMs)),
        })}
      </p>
    </div>
  );

  return panel;
}

/** Properties of {@link CrewRow}. */
interface CrewRowProps {
  readonly entry: CrewEntry;
  readonly checked: boolean;
  readonly timeText: (seconds: number) => string;
  readonly onChange: (checked: boolean) => void;
}

/**
 * One person of the crew: the choice, the local state as a badge and events not sent yet.
 *
 * @param props - Component properties.
 * @returns The list item.
 */
function CrewRow(props: CrewRowProps): JSX.Element {
  const { t } = useI18n();
  const { member, state: clockState, since, pending } = props.entry;
  const state =
    clockState === "Off" || since === null
      ? t("team.state.Off")
      : t(`team.state.${clockState}`, { time: props.timeText(since) });

  const row = (
    <li>
      <Checkbox
        checked={props.checked}
        onChange={props.onChange}
        label={
          <>
            {member.displayName} <span className={styles["muted"]}>({member.personnelNumber})</span>{" "}
            <Badge tone={stateTone[clockState]}>{state}</Badge>{" "}
            {pending === 0 ? null : (
              <Badge tone="warning">{t("crew.pending", { count: pending })}</Badge>
            )}
          </>
        }
      />
    </li>
  );

  return row;
}

/** Properties of {@link CrewConflicts}. */
interface CrewConflictsProps {
  readonly conflicts: readonly LocalEvent[];
  readonly members: readonly CrewMember[];
}

/**
 * Events on behalf the server rejected, with the person and the reason by its code (UX-107); the
 * person concerned records missing times through a correction request.
 *
 * @param props - Component properties.
 * @returns The list or nothing.
 */
function CrewConflicts(props: CrewConflictsProps): JSX.Element | null {
  const { t, languageTag } = useI18n();
  const purchasePaths = usePurchasePaths();
  const { store } = useOffline();

  if (props.conflicts.length === 0) {
    return null;
  }

  const names = new Map(props.members.map((member) => [member.employeeId, member.displayName]));
  const list = (
    <div className={styles["stack"]}>
      <h3>{t("crew.conflicts")}</h3>
      <p className={styles["muted"]}>{t("crew.conflictHint")}</p>
      <ul className={clockStyles["list"]}>
        {props.conflicts.map((event) => (
          <li key={event.id}>
            <Alert tone="danger" live="off">
              <p>
                {t("crew.conflict", {
                  action: t(`clock.action.${event.kind}`),
                  name: names.get(event.employeeId ?? "") ?? t("crew.unknownPerson"),
                  time: formatDateTime(event.occurredAt, languageTag, event.timeZoneId),
                  reason: describeErrorCode(event.error?.code ?? null, t, purchasePaths),
                })}
              </p>
              <button
                type="button"
                className={styles["button"]}
                onClick={() => {
                  void store.dismissRejected(event.id);
                }}
              >
                {t("clock.conflictDismiss")}
              </button>
            </Alert>
          </li>
        ))}
      </ul>
    </div>
  );

  return list;
}
